/**
 * React Query Hooks for Polls
 * Custom hooks for poll data fetching and mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as pollsService from '../services/polls.service';
import type { Poll, CreatePollRequest } from '../types/api.types';

/**
 * Query key factory for polls
 */
export const pollKeys = {
  all: ['polls'] as const,
  poll: (id: string) => ['polls', id] as const,
  results: (id: string) => ['polls', id, 'results'] as const,
};

/**
 * Get poll by ID
 */
export function usePoll(pollId: string | undefined) {
  return useQuery({
    queryKey: pollKeys.poll(pollId || ''),
    queryFn: () => {
      if (!pollId) {
        throw new Error('Poll ID required');
      }
      return pollsService.getPoll(pollId);
    },
    enabled: !!pollId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Get poll results
 */
export function usePollResults(pollId: string | undefined) {
  return useQuery({
    queryKey: pollKeys.results(pollId || ''),
    queryFn: () => {
      if (!pollId) {
        throw new Error('Poll ID required');
      }
      return pollsService.getPollResults(pollId);
    },
    enabled: !!pollId,
    staleTime: 10000, // 10 seconds
  });
}

/**
 * Create poll mutation
 */
export function useCreatePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pollData: CreatePollRequest) => pollsService.createPoll(pollData),
    onSuccess: (poll: Poll) => {
      // Add poll to cache
      queryClient.setQueryData(pollKeys.poll(poll.id), poll);

      // Invalidate messages to refresh with poll
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['channelMessages'] });
      queryClient.invalidateQueries({ queryKey: ['dmMessages'] });

      toast.success('Анкетата е създадена');
    },
    onError: (error) => {
      console.error('Failed to create poll:', error);
      toast.error('Грешка при създаване на анкета');
    },
  });
}

/**
 * Vote on poll mutation
 */
export function useVoteOnPoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pollId,
      optionIds,
    }: {
      pollId: string;
      optionIds: string[];
    }) => pollsService.voteOnPoll(pollId, { optionIds }),
    onMutate: async ({ pollId, optionIds }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: pollKeys.poll(pollId) });

      // Snapshot previous value
      const previousPoll = queryClient.getQueryData<Poll>(pollKeys.poll(pollId));

      // Optimistically update poll
      if (previousPoll) {
        queryClient.setQueryData<Poll>(pollKeys.poll(pollId), {
          ...previousPoll,
          userVotes: optionIds,
        });
      }

      return { previousPoll };
    },
    onError: (err, { pollId }, context) => {
      // Rollback on error
      if (context?.previousPoll) {
        queryClient.setQueryData(pollKeys.poll(pollId), context.previousPoll);
      }
      console.error('Failed to vote on poll:', err);
      toast.error('Грешка при гласуване');
    },
    onSuccess: (poll: Poll) => {
      // Update poll in cache
      queryClient.setQueryData(pollKeys.poll(poll.id), poll);
      queryClient.setQueryData(pollKeys.results(poll.id), poll);

      toast.success('Гласът е регистриран');
    },
  });
}

/**
 * Change vote mutation
 */
export function useChangeVote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pollId,
      optionIds,
    }: {
      pollId: string;
      optionIds: string[];
    }) => pollsService.changeVote(pollId, { optionIds }),
    onMutate: async ({ pollId, optionIds }) => {
      await queryClient.cancelQueries({ queryKey: pollKeys.poll(pollId) });

      const previousPoll = queryClient.getQueryData<Poll>(pollKeys.poll(pollId));

      if (previousPoll) {
        queryClient.setQueryData<Poll>(pollKeys.poll(pollId), {
          ...previousPoll,
          userVotes: optionIds,
        });
      }

      return { previousPoll };
    },
    onError: (err, { pollId }, context) => {
      if (context?.previousPoll) {
        queryClient.setQueryData(pollKeys.poll(pollId), context.previousPoll);
      }
      console.error('Failed to change vote:', err);
      toast.error('Грешка при промяна на глас');
    },
    onSuccess: (poll: Poll) => {
      queryClient.setQueryData(pollKeys.poll(poll.id), poll);
      queryClient.setQueryData(pollKeys.results(poll.id), poll);

      toast.success('Гласът е променен');
    },
  });
}

/**
 * Close poll mutation
 */
export function useClosePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pollId: string) => pollsService.closePoll(pollId),
    onSuccess: (poll: Poll) => {
      // Update poll in cache
      queryClient.setQueryData(pollKeys.poll(poll.id), poll);
      queryClient.setQueryData(pollKeys.results(poll.id), poll);

      toast.success('Анкетата е затворена');
    },
    onError: (error) => {
      console.error('Failed to close poll:', error);
      toast.error('Грешка при затваряне на анкета');
    },
  });
}
